import type { SpeakOptions, SpeakSource, SpeechPlayer } from '../contracts';
import { sharedAudioContext } from '../audio-context';
import { speechParts, type SpeakableSentence } from './speakable';
import { createReplayCache, messageIdFor, sourceKey } from './cache';
import { createAudioSchedule } from './schedule';
import { audioRequest, PlaybackError, type AudioRequestResult } from './audio-request';
import { clearLaneTiming, markLaneSpokenText } from '../lane-timing';
import { resetLanePlaybackTiming } from './lane-playback-timing';
import { highlightSentence } from './highlight';
import { createPcmDecoder, type PcmStream } from './pcm';
import { SessionChangedError, SessionUnverifiedError } from '../../lib/session-guard';

/** voice-structural: typed playback error codes eligible for per-sentence recovery, never transcript words. */
const SENTENCE_FAILURES: ReadonlySet<string> = new Set([
  'tts_unavailable',
  'tts_audio_empty',
  'tts_empty_audio',
  'tts_audio_too_large',
  'tts_language_unsupported',
]);

type Pipeline = {
  key: string;
  source: SpeakSource;
  fetch: typeof fetch;
  options: SpeakOptions;
  sentences: SpeakableSentence[];
  done: boolean;
  abort: AbortController;
  requested: number;
  scheduled: number;
  completed: number;
  pending: number;
  /** A sentence that could not be read is null: it is skipped and the rest still play. */
  ready: Map<number, AudioRequestResult | null>;
  /** Failed sentences in a row, and in all; the last failure is the notice when nothing played. */
  failures: number;
  skipped: number;
  lastError: string | null;
  consuming: boolean;
  schedule: ReturnType<typeof createAudioSchedule> | null;
  resolve: () => void;
  completion: Promise<void>;
};
export type ReadAloudPlayer = SpeechPlayer & {
  bindScope(scope: string): () => void;
  resume(): Promise<void>;
  readMessage(threadId: string, messageId: string, options: SpeakOptions): Promise<void>;
  notice(): string | null;
  currentMessage(): string | null;
  version(): number;
};
export function createSpeechPlayer({
  fetchImpl,
  audioContextFactory = sharedAudioContext,
  maxCache = 5,
  maxCacheBytes = 20 * 1024 * 1024,
  highlight = highlightSentence,
}: {
  fetchImpl?: typeof fetch;
  audioContextFactory?: () => AudioContext;
  maxCache?: number;
  maxCacheBytes?: number;
  highlight?: typeof highlightSentence;
} = {}): ReadAloudPlayer {
  const cache = createReplayCache(maxCache, maxCacheBytes);
  const listeners = new Set<() => void>();
  const leases = new Set<symbol>();
  const mutedFeeds = new Set<string>();
  const finishedFeeds = new Set<string>();
  const remember = (set: Set<string>, key: string) => {
    set.add(key);
    while (set.size > 20) set.delete(set.values().next().value!);
  };
  let identity: string | null = null;
  let context: AudioContext | null = null;
  let active: Pipeline | null = null;
  let preparing: AbortController | null = null;
  let state: ReturnType<SpeechPlayer['state']> = 'idle';
  let notice: string | null = null;
  let message: string | null = null;
  let version = 0;
  let duckDb = 0;
  const notify = () => {
    version++;
    for (const listener of listeners) listener();
  };
  const audioContext = () => (context ??= audioContextFactory());
  const live = (pipe: Pipeline) => active === pipe && !pipe.abort.signal.aborted;
  function stop(reason: Parameters<SpeechPlayer['stop']>[0]) {
    preparing?.abort();
    preparing = null;
    const old = active;
    active = null;
    old?.abort.abort();
    if (old?.source.kind === 'message') remember(mutedFeeds, old.key);
    const position =
      old?.schedule?.stop() ??
      (old
        ? {
            messageId: messageIdFor(old.source),
            sentenceIndex: 0,
            charOffset: 0,
          }
        : null);
    old?.resolve();
    if (reason === 'exit') {
      cache.clear();
      mutedFeeds.clear();
      finishedFeeds.clear();
      message = null;
      notice = null;
    }
    state = 'idle';
    notify();
    return position;
  }
  function fail(pipe: Pipeline, error: unknown) {
    if (!live(pipe)) return;
    stop('user');
    notice = error instanceof PlaybackError ? error.code : 'tts_unavailable';
    notify();
  }
  /** Only the voice failing to read one sentence is skipped; the rest of the answer still plays. Anything else (a
   * changed session, lost access, a changed source, billing) stops reading at once, and so do two failed sentences
   * in a row: then the voice is down, not one sentence. */
  function skip(pipe: Pipeline, index: number, error: unknown) {
    if (!live(pipe)) return;
    if (!(error instanceof PlaybackError) || !SENTENCE_FAILURES.has(error.code))
      return fail(pipe, error);
    pipe.failures++;
    pipe.skipped++;
    pipe.lastError = error.code;
    if (pipe.failures >= 2) return fail(pipe, error);
    pipe.ready.set(index, null);
  }
  function finished(pipe: Pipeline) {
    if (!live(pipe) || !pipe.done || pipe.completed !== pipe.sentences.length || pipe.pending)
      return;
    if (pipe.skipped && pipe.skipped === pipe.sentences.length)
      return fail(pipe, new PlaybackError(pipe.lastError!));
    if (identity)
      cache.put({
        key: pipe.key,
        source: pipe.source,
        options: pipe.options,
        sentences: pipe.sentences,
      });
    if (pipe.source.kind === 'message') remember(finishedFeeds, pipe.key);
    active = null;
    state = 'idle';
    pipe.schedule?.stop();
    pipe.resolve();
    notify();
  }
  function receive(pipe: Pipeline, index: number, clip: AudioRequestResult) {
    if (!live(pipe)) return;
    pipe.ready.set(index, clip);
  }
  async function consumePcm(
    pipe: Pipeline,
    index: number,
    stream: PcmStream,
    profile: AudioRequestResult['profile'],
    playbackId: string | null,
  ) {
    // PCM is consumed as chunks arrive; the iterator is deliberately not buffered.
    const decoder = createPcmDecoder(audioContext(), stream.sampleRate);
    const chunkBytes = Math.floor(stream.sampleRate / 10) * 2;
    let emitted = false;
    try {
      for await (const bytes of stream.chunks) {
        for (let offset = 0; offset < bytes.byteLength; offset += chunkBytes) {
          if (!live(pipe)) return;
          await pipe.schedule!.waitForCapacity();
          if (!live(pipe)) return;
          const decoded = decoder.decode(bytes.subarray(offset, offset + chunkBytes));
          if (!decoded) continue;
          pipe.schedule!.append(index, pipe.sentences[index], decoded, false, profile, playbackId);
          // Audio, not response headers, is what proves the voice is reading again.
          emitted = true;
          pipe.failures = 0;
          state = 'speaking';
          notify();
        }
      }
      decoder.finish();
      if (!emitted) throw new PlaybackError('tts_empty_audio');
      pipe.schedule!.complete(index);
      pipe.scheduled++;
    } catch (error) {
      if (!live(pipe)) return;
      if (error instanceof SessionChangedError || error instanceof SessionUnverifiedError)
        return fail(pipe, error);
      // A stream that breaks keeps what it already played; one that played nothing is skipped.
      if (emitted) {
        pipe.schedule!.complete(index);
        pipe.scheduled++;
      } else
        skip(
          pipe,
          index,
          error instanceof PlaybackError
            ? error
            : // The audio stream breaking is the voice failing this sentence.
              new PlaybackError('tts_unavailable'),
        );
    } finally {
      pipe.consuming = false;
      if (live(pipe)) pump(pipe);
    }
  }
  async function fetchClip(pipe: Pipeline, index: number) {
    try {
      const signal = pipe.abort.signal;
      const clip = await audioRequest(
        pipe.fetch,
        audioContext(),
        pipe.source,
        index,
        pipe.options,
        signal,
      );
      receive(pipe, index, clip);
    } catch (error) {
      skip(pipe, index, error);
    } finally {
      pipe.pending--;
      if (live(pipe)) pump(pipe);
    }
  }
  function pump(pipe: Pipeline) {
    if (!live(pipe)) return;
    const ctx = audioContext();
    if (ctx.state !== 'running') {
      state = 'paused';
      notice = 'tap_to_listen';
      notify();
      return;
    }
    notice = null;
    pipe.schedule ??= createAudioSchedule(
      ctx,
      messageIdFor(pipe.source),
      () => {
        if (!live(pipe)) return;
        pipe.completed++;
        pump(pipe);
      },
      (sentence) => highlight(messageIdFor(pipe.source), sentence),
    );
    pipe.schedule.duck(duckDb);
    while (!pipe.consuming && pipe.ready.has(pipe.scheduled)) {
      const index = pipe.scheduled++;
      const clip = pipe.ready.get(index);
      pipe.ready.delete(index);
      if (!clip) {
        pipe.completed++;
        continue;
      }
      if ('kind' in clip.audio) {
        pipe.scheduled--;
        pipe.consuming = true;
        void consumePcm(pipe, index, clip.audio, clip.profile, clip.playbackId);
        break;
      }
      pipe.schedule.append(
        index,
        pipe.sentences[index],
        clip.audio,
        true,
        clip.profile,
        clip.playbackId,
      );
      pipe.failures = 0;
    }
    state = pipe.schedule.pending() ? 'speaking' : 'loading';
    notify();
    // Two audio buffers ahead of what has finished, fetched one at a time: one sentence request (or stream) in
    // flight, so a voice never gets two concurrent requests from one reader.
    while (
      pipe.requested < pipe.sentences.length &&
      pipe.requested - pipe.completed < 2 &&
      !pipe.pending &&
      !pipe.consuming
    ) {
      const index = pipe.requested++;
      pipe.pending++;
      void fetchClip(pipe, index);
    }
    finished(pipe);
  }
  function begin(
    source: SpeakSource,
    sentences: SpeakableSentence[],
    done: boolean,
    options: SpeakOptions,
    requestFetch = fetchImpl ?? globalThis.fetch,
  ) {
    stop('user');
    resetLanePlaybackTiming();
    let resolve!: () => void;
    const completion = new Promise<void>((finish) => {
      resolve = finish;
    });
    const pipe: Pipeline = {
      key: sourceKey(source, options),
      source,
      fetch: requestFetch,
      options,
      sentences,
      done,
      abort: new AbortController(),
      requested: 0,
      scheduled: 0,
      completed: 0,
      pending: 0,
      ready: new Map(),
      failures: 0,
      skipped: 0,
      lastError: null,
      consuming: false,
      schedule: null,
      resolve,
      completion,
    };
    active = pipe;
    message = messageIdFor(source);
    notice = null;
    return pipe;
  }
  async function resume() {
    try {
      await audioContext().resume();
      if (active) pump(active);
    } catch {
      notice = 'tap_to_listen';
      state = 'paused';
      notify();
    }
  }
  function feed(source: SpeakSource, text: string, done: boolean, options: SpeakOptions) {
    const sentences = speechParts(
      source,
      text,
      source.kind === 'message' && source.channel === 'spoken' ? true : done,
    );
    const key = sourceKey(source, options);
    if (mutedFeeds.has(key) || finishedFeeds.has(key)) return;
    let pipe = active;
    if (!pipe || pipe.key !== key) pipe = begin(source, sentences, done, options);
    else {
      const changed = pipe.sentences
        .slice(0, pipe.requested)
        .some(
          (old, index) => old.text !== sentences[index]?.text || old.cue !== sentences[index]?.cue,
        );
      if (changed) {
        fail(pipe, new PlaybackError('tts_source_mismatch'));
        return;
      }
      pipe.sentences = sentences;
      pipe.done = done;
      if (source.kind === 'message' && source.streamId) pipe.source = source;
    }
    pump(pipe);
  }
  async function readMessage(threadId: string, messageId: string, options: SpeakOptions) {
    clearLaneTiming();
    markLaneSpokenText();
    stop('user');
    const controller = new AbortController();
    // Capture the installed guard for this action, not the unguarded boot fetch.
    const requestFetch = fetchImpl ?? globalThis.fetch;
    preparing = controller;
    message = messageId;
    state = 'loading';
    notice = null;
    notify();
    try {
      // Called directly by the click handler: resume starts before the first await or request.
      await audioContext().resume();
      controller.signal.throwIfAborted();
      const response = await requestFetch('/api/v1/speech/read-source', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ threadId, messageId }),
        signal: controller.signal,
      });
      const result = await response.json();
      controller.signal.throwIfAborted();
      if (!response.ok) throw new PlaybackError(result.reason ?? result.error ?? 'tts_unavailable');
      if (!result.text?.trim() || typeof result.revision !== 'string')
        throw new PlaybackError('tts_source_mismatch');
      preparing = null;
      const source: SpeakSource = {
        kind: 'stored_message',
        threadId,
        messageId,
        revision: result.revision,
        format: 'passages',
      };
      const pipe = begin(
        source,
        speechParts(source, result.text, true),
        true,
        options,
        requestFetch,
      );
      pump(pipe);
      await pipe.completion;
    } catch (error) {
      if (controller.signal.aborted) return;
      preparing = null;
      state = 'idle';
      notice =
        context?.state !== 'running'
          ? 'tap_to_listen'
          : error instanceof PlaybackError
            ? error.code
            : 'tts_unavailable';
      notify();
    }
  }
  const player: ReadAloudPlayer = {
    feed,
    readMessage,
    resume,
    stop,
    async speak(source, text, options, signal) {
      if (signal?.aborted) return;
      if (source.kind === 'confirmation') {
        clearLaneTiming();
        markLaneSpokenText();
      }
      const parts = speechParts(source, text, true);
      const pipe = begin(source, parts, true, options);
      const abort = () => {
        if (active === pipe) stop('user');
      };
      signal?.addEventListener('abort', abort, { once: true });
      try {
        pump(pipe);
        await pipe.completion;
      } finally {
        signal?.removeEventListener('abort', abort);
      }
    },
    replay(messageId) {
      const entry = cache.get(messageId);
      if (!entry) return false;
      clearLaneTiming();
      markLaneSpokenText();
      const pipe = begin(entry.source, entry.sentences, true, entry.options);
      // Revalidate every source at the server, including confirmation revisions.
      // The authorized server audio cache avoids another provider charge.
      pump(pipe);
      return true;
    },
    bindScope(scope) {
      if (identity !== scope) {
        if (identity !== null) stop('exit');
        identity = scope;
        leases.clear();
      }
      const token = Symbol();
      leases.add(token);
      return () => {
        leases.delete(token);
        if (identity === scope && leases.size === 0) {
          stop('exit');
          identity = null;
        }
      };
    },
    duck(db) {
      duckDb = db;
      active?.schedule?.duck(db);
    },
    unduck() {
      duckDb = 0;
      active?.schedule?.duck(0);
    },
    state: () => state,
    playbackLevel: () => active?.schedule?.playbackLevel() ?? 0,
    echoLevel: (samples, rate, endedAgoMs) =>
      active?.schedule?.echoLevel(samples, rate, endedAgoMs) ?? 0,
    notice: () => notice,
    currentMessage: () => message,
    version: () => version,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
  return player;
}
export const speechPlayer = createSpeechPlayer();
if (typeof document !== 'undefined')
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) speechPlayer.stop('hidden');
  });
